import { LegalLayout, LegalSection } from "./LegalLayout";

const LAST_UPDATED = "6 de mayo de 2026";

export function PrivacyPolicyPage() {
  return (
    <>
      <title>Política de Privacidad · HATO</title>
      <meta
        name="description"
        content="Cómo HATO recolecta, usa y protege tus datos. Conforme a la Ley 8968 de Costa Rica."
      />
      <meta name="robots" content="index, follow" />

      <LegalLayout title="Política de Privacidad" lastUpdated={LAST_UPDATED}>
        <p>
          En HATO entendemos que los datos que cargás sobre tu finca, tu hato y tu operación
          contable son sensibles. Esta política explica de manera clara cómo los recolectamos, cómo
          los usamos, con quién los compartimos y qué derechos tenés sobre ellos.
        </p>

        <p>
          Esta política aplica a todas las personas que utilizan la plataforma HATO, ya sea como
          dueños de cuenta, miembros invitados o solicitantes de acceso. Al usar la plataforma
          aceptás los términos descritos en este documento.
        </p>

        <LegalSection number={1} title="Quiénes somos">
          <p>
            HATO es una plataforma digital de gestión ganadera contable, diseñada para fincas
            lecheras en Costa Rica. Operamos bajo el nombre comercial HATO y nuestro canal oficial
            de contacto es{" "}
            <a
              href="mailto:info@mihato.co"
              className="text-white underline-offset-4 hover:underline"
            >
              info@mihato.co
            </a>
            . Para cualquier consulta sobre el tratamiento de tus datos personales podés escribirnos
            a esa dirección.
          </p>
        </LegalSection>

        <LegalSection number={2} title="Qué información recolectamos">
          <p>
            Recolectamos únicamente la información necesaria para operar la plataforma y brindarte
            el servicio. La información que manejamos se agrupa en cuatro categorías:
          </p>
          <ul className="ml-2 flex list-inside list-disc flex-col gap-2 marker:text-neutral-500">
            <li>
              <span className="text-white">Datos de identidad:</span> nombre, correo electrónico y,
              si lo proporcionás voluntariamente, número de teléfono. Estos datos se piden al
              momento de solicitar acceso o al ser invitado a una cuenta.
            </li>
            <li>
              <span className="text-white">Datos del hato:</span> información operativa que cargás
              sobre tus animales, lotes, eventos reproductivos y sanitarios, etapas de vida y demás
              registros relacionados con la gestión del ganado.
            </li>
            <li>
              <span className="text-white">Datos contables:</span> costos cargados, clasificaciones
              de cuenta, asignaciones de costo a lotes o animales y los exports contables que
              generás para tu contador.
            </li>
            <li>
              <span className="text-white">Datos técnicos:</span> dirección IP, tipo de dispositivo
              y navegador, fecha y hora de acceso, y registros de actividad necesarios para
              diagnóstico, seguridad y prevención de abuso.
            </li>
          </ul>
        </LegalSection>

        <LegalSection number={3} title="Cómo usamos la información">
          <p>Utilizamos la información que recolectamos exclusivamente para:</p>
          <ul className="ml-2 flex list-inside list-disc flex-col gap-2 marker:text-neutral-500">
            <li>Operar y mantener la plataforma.</li>
            <li>Autenticar tu acceso de forma segura mediante enlaces de un solo uso al correo.</li>
            <li>
              Permitirte gestionar tu hato, registrar costos, clasificar movimientos contables y
              generar reportes y exports.
            </li>
            <li>
              Diagnosticar y resolver problemas técnicos, monitorear el rendimiento del servicio y
              prevenir abusos.
            </li>
            <li>
              Enviarte comunicaciones operativas relacionadas con tu cuenta (por ejemplo, cambios
              importantes en el servicio o avisos de seguridad). No te enviamos comunicaciones de
              marketing sin tu consentimiento expreso.
            </li>
          </ul>
          <p>
            No utilizamos tus datos para entrenar modelos comerciales de terceros ni los
            comercializamos bajo ninguna forma.
          </p>
        </LegalSection>

        <LegalSection number={4} title="Con quién compartimos información">
          <p>
            HATO se apoya en proveedores de infraestructura cloud para autenticación, hosting,
            almacenamiento y envío de correos. Estos proveedores actúan estrictamente como
            procesadores de datos bajo nuestras instrucciones y solo acceden a la información
            necesaria para prestar su servicio. No comercializan ni utilizan tus datos para fines
            propios.
          </p>
          <p>
            No compartimos tus datos con terceros para fines de publicidad, marketing o analítica de
            comportamiento. Únicamente revelaremos información personal cuando lo exija una
            autoridad competente mediante orden judicial o requerimiento legal válido.
          </p>
        </LegalSection>

        <LegalSection number={5} title="Cookies y sesión">
          <p>
            Utilizamos cookies estrictamente necesarias para mantener tu sesión iniciada y recordar
            preferencias básicas de la interfaz. No utilizamos cookies de publicidad, tracking de
            comportamiento ni servicios de analítica de terceros que perfilen tu navegación fuera de
            HATO.
          </p>
        </LegalSection>

        <LegalSection number={6} title="Cómo protegemos tu información">
          <p>
            Aplicamos prácticas estándar de la industria para proteger los datos que nos confiás.
            Las comunicaciones entre tu navegador y la plataforma viajan por canales cifrados. La
            autenticación se realiza sin contraseña, mediante enlaces de un solo uso enviados a tu
            correo, lo que reduce el riesgo de robo de credenciales. Los datos se almacenan en bases
            de datos protegidas con controles de acceso restringido al equipo operativo de HATO.
          </p>
          <p>
            Reconocemos que ningún sistema digital es completamente invulnerable. Si llegamos a
            detectar un incidente que comprometa la seguridad de tus datos personales, te
            notificaremos por los canales habituales y reportaremos a las autoridades cuando
            corresponda.
          </p>
        </LegalSection>

        <LegalSection number={7} title="Retención de información">
          <p>
            Conservamos tu información mientras tu cuenta esté activa o mientras sea necesaria para
            prestarte el servicio. Si das de baja tu cuenta, eliminaremos tus datos personales y
            operativos en un plazo razonable, salvo aquella información que la legislación
            costarricense nos obligue a retener (por ejemplo, registros contables o fiscales con
            plazos legales mínimos de conservación).
          </p>
          <p>
            Podés solicitar la baja de tu cuenta y la eliminación de tus datos escribiendo a{" "}
            <a
              href="mailto:info@mihato.co"
              className="text-white underline-offset-4 hover:underline"
            >
              info@mihato.co
            </a>
            .
          </p>
        </LegalSection>

        <LegalSection number={8} title="Tus derechos">
          <p>
            De acuerdo con la Ley 8968 de Protección de la Persona frente al Tratamiento de sus
            Datos Personales y su Reglamento (Decreto Ejecutivo 37554), tenés los siguientes
            derechos sobre tus datos personales:
          </p>
          <ul className="ml-2 flex list-inside list-disc flex-col gap-2 marker:text-neutral-500">
            <li>
              <span className="text-white">Acceso:</span> conocer qué datos tuyos tratamos y con qué
              finalidad.
            </li>
            <li>
              <span className="text-white">Rectificación:</span> solicitar la corrección de datos
              inexactos o incompletos.
            </li>
            <li>
              <span className="text-white">Supresión:</span> pedir la eliminación de tus datos
              cuando ya no sean necesarios para la finalidad para la que fueron recolectados.
            </li>
            <li>
              <span className="text-white">Oposición:</span> oponerte al tratamiento de tus datos
              por motivos legítimos.
            </li>
          </ul>
          <p>
            Para ejercer cualquiera de estos derechos escribinos a{" "}
            <a
              href="mailto:info@mihato.co"
              className="text-white underline-offset-4 hover:underline"
            >
              info@mihato.co
            </a>{" "}
            y respondemos en un plazo razonable. Podés además acudir a la Agencia de Protección de
            Datos de los Habitantes (PRODHAB) si considerás que tus derechos no han sido atendidos.
          </p>
        </LegalSection>

        <LegalSection number={9} title="Cambios a esta política">
          <p>
            Podemos actualizar esta política para reflejar cambios en la plataforma, en nuestras
            prácticas operativas o en la legislación aplicable. Cuando hagamos cambios relevantes te
            notificaremos a través de la plataforma o por correo electrónico, e indicaremos la fecha
            de la nueva versión al inicio del documento. El uso continuado del servicio después de
            la notificación implica la aceptación de la versión actualizada.
          </p>
        </LegalSection>

        <p className="text-neutral-500 text-sm">
          Responsable de tratamiento: HATO. Para consultas sobre esta política o sobre el
          tratamiento de tus datos, escribinos a{" "}
          <a
            href="mailto:info@mihato.co"
            className="text-neutral-300 underline-offset-4 hover:text-white hover:underline"
          >
            info@mihato.co
          </a>
          .
        </p>
      </LegalLayout>
    </>
  );
}
